package com.eladfinish.bible.contest.screens.names

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.sp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.arch.ViewModelStore
import com.eladfinish.bible.contest.common.usecases.GetHighlightedVerseMarkupRangeUseCase
import com.eladfinish.bible.contest.common.usecases.MakeOtiotPasukSmallerUseCase
import com.eladfinish.bible.contest.constants.StringConstants.DAGESH
import com.eladfinish.bible.contest.constants.StringConstants.Regex.ALEF_TAV
import com.eladfinish.bible.contest.constants.StringConstants.Regex.NIKUD_CHARS
import com.eladfinish.bible.contest.constants.StringConstants.Regex.TEAMIM_CHARS
import com.eladfinish.bible.contest.constants.StringConstants.SPACE_MAKAF
import com.eladfinish.bible.contest.data.ChapterData
import com.eladfinish.bible.contest.data.common.ClipboardCopier
import com.eladfinish.bible.contest.domain.BooksRepository.bookNamesHebrew
import com.eladfinish.bible.contest.extensions.end
import com.eladfinish.bible.contest.extensions.replaceRegex
import com.eladfinish.bible.contest.extensions.runIf
import com.eladfinish.bible.contest.extensions.start
import com.eladfinish.bible.contest.hebrew_aleph_bet
import com.eladfinish.bible.contest.screens.names.NamesAction.GenderSelected
import com.eladfinish.bible.contest.screens.names.NamesAction.LetterSelected
import com.eladfinish.bible.contest.screens.names.NamesState.DialogState
import com.eladfinish.bible.contest.screens.names.data.Gender
import com.eladfinish.bible.contest.screens.names.data.NameItem
import com.eladfinish.bible.contest.screens.names.data.NameReference
import com.eladfinish.bible.contest.screens.names.data.NamesResult
import com.eladfinish.bible.contest.text.SpanType
import com.eladfinish.bible.contest.text.SpannedRange
import com.eladfinish.bible.contest.ui.getAnnotatedStringForLink
import com.eladfinish.bible.contest.utils.gematriaWord
import dev.icerock.moko.resources.desc.desc
import io.github.aakira.napier.Napier
import io.ktor.http.HttpStatusCode
import kotlinx.collections.immutable.toImmutableList
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow

class NamesStore(
    private val namesLoader: NamesLoader,
    private val makeOtiotPasukSmallerUseCase: MakeOtiotPasukSmallerUseCase,
    private val getHighlightedVerseMarkupRangeUseCase: GetHighlightedVerseMarkupRangeUseCase,
    private val clipboardCopier: ClipboardCopier,
) : ViewModelStore<NamesState, NamesAction, NamesEffect>(
    tag = "NamesStore",
) {

    private val defaultState: NamesState
        get() = NamesState(
            isLoading = false,
            genderList = Gender.entries.toImmutableList(),
            alephBet = SharedRes.strings.hebrew_aleph_bet.desc(),
            letterLines = 2,
            selectedGender = Gender.MALE,
            selectedLetter = null,
            annotatedString = AnnotatedString(text = ""),
            nameList = emptyList<NameItem>().toImmutableList(),
            dialogState = DialogState.None,
        )

    @ExperimentalCoroutinesApi
    override val stateFlow = MutableStateFlow(defaultState)

    override fun handleAction(action: NamesAction): NamesState =
        when (action) {
            is GenderSelected -> {
                getNames(gender = action.gender, letter = currentState.selectedLetter)
                currentState.copy(
                    selectedGender = action.gender,
                )
            }

            is LetterSelected -> {
                getNames(gender = currentState.selectedGender, letter = action.letter)
                currentState.copy(
                    selectedLetter = action.letter,
                )
            }

            is NamesAction.NameReferenceSelected -> {
                val chapterData = handleNameReferenceSelected(action.name, action.nameReference)
                currentState.copy(dialogState = DialogState.ChapterDialog(chapterData = chapterData))
            }

            is NamesAction.BrowseSelected -> {
                currentState.copy(dialogState = DialogState.MenuDialog(name = action.name, nameReferences = action.nameReferences))
            }

            is NamesAction.InternalLinkClicked -> {
                val (gender, letter, name, _, _) = action.internalNameLink
                getNames(gender = gender, letter = letter, targetName = name)
                currentState.copy(
                    selectedGender = gender,
                    selectedLetter = letter,
                )
            }

            is NamesAction.OnItemCopied -> handleOnItemCopied(action.text)
            NamesAction.HideDialog -> handleHideDialog()
        }

    private fun handleHideDialog(): NamesState {
        return currentState.copy(dialogState = DialogState.None)
    }

    private fun handleNameReferenceSelected(name: String, nameReference: NameReference): ChapterData {
        val (content, markupRange) = getHighlightedVerseMarkupRangeUseCase(
            bookIndex = nameReference.bookAsNumber,
            chapter = nameReference.chapter,
            otVerse = nameReference.verse,
        )

        val nameOccurrences: (String, Boolean) -> List<TextRange> = { str, shouldOptionalizeVavAndYud ->
            str.trim()
                .makeDageshFirstNikud()
                .withOptionalTeamim()
                .runIf(condition = shouldOptionalizeVavAndYud) {
                    makeVavAndYudOptional()
                }
                .replace(oldValue = " ", newValue = SPACE_MAKAF)
                .replace(oldValue = DAGESH.toString(), newValue = "$DAGESH?") // make Dagesh optional
                .toRegex()
                .findAll(content).map { matchResult ->
                    TextRange(start = matchResult.start, end = matchResult.end)
                }.toList()
        }

        val spannedRanges = name.split(", ").map {
            nameOccurrences(it, false).takeIf { list -> list.isNotEmpty() }
                ?: nameOccurrences(it, true)
        }.flatten().map {
            SpannedRange(
                spanType = SpanType.BackgroundColor(Color.Green),
                textRange = it,
            )
        }

        val spannedRangeList = buildList {
            // ORDER IS MATTER!
            addAll(elements = makeOtiotPasukSmallerUseCase(charSequence = content))
            add(SpannedRange(spanType = SpanType.BackgroundColor(Color.Yellow), textRange = markupRange))
            addAll(elements = spannedRanges)
        }

        return ChapterData(
            bookIndex = bookNamesHebrew.indexOf(nameReference.book),
            chapterIndex = nameReference.chapter.gematriaWord(),
            content = content,
            spannedRangeList = spannedRangeList.toImmutableList(),
        )
    }

    private fun handleOnItemCopied(text: String): NamesState {
        clipboardCopier.copy(text)
        return currentState
    }

    // The gender is a parameter because the load below starts on another thread before handleAction
    // returns, so currentState there may still hold the gender from before the action.
    private fun getNames(gender: Gender, letter: Char?, targetName: String? = null) {
        if (letter != null) {
            updateState { state ->
                state.copy(
                    isLoading = true,
                )
            }
            launchEffect {
                val namesResult: NamesResult = namesLoader.invoke(
                    gender = gender,
                    letter = letter.toString()
                )

                val nameList = namesResult.apiResult.dataOrNull().orEmpty().toImmutableList()
                updateState { state ->
                    state.copy(
                        isLoading = false,
                        annotatedString = getAnnotatedString(namesResult),
                        nameList = nameList,
                    )
                }

                when (namesResult.errorCause) {
                    is ErrorCause.Http -> {
                        when (namesResult.errorCause.statusCode) {
                            HttpStatusCode.NotFound -> {
                                sideEffectFlow.emit(NamesEffect.NoNameFound)
                            }

                            HttpStatusCode.OK -> {
                                launchEffect {
                                    if (targetName == null) {
                                        sideEffectFlow.emit(NamesEffect.ItemsLoaded)
                                    } else {
                                        val targetIndex = nameList.indexOfFirst {
                                            it.name == targetName
                                                    || it.name.startsWith(targetName)
                                                    || targetName.startsWith(it.name)
                                            // "אֲבִיָּה, אֲבִי" מופיע בשמות הנשים, אך בשמות הגברים מופיע רק "אֲבִיָּה"
                                            // לכן נדרש startsWith הדדי כי לא תמיד יש שוויון מדויק
                                        }.takeIf { it != -1 } ?: 0
                                        sideEffectFlow.emit(NamesEffect.ScrollToItem(index = targetIndex))
                                        // TODO: להבין למה צריך את זה
                                    }
                                }
                            }
                        }
                    }

                    ErrorCause.NoInternet -> {
                        sideEffectFlow.emit(NamesEffect.NoInternet)
                    }
                }

                Napier.d("namesResult_statusCode ${namesResult.errorCause}, getNamesUseCase_ $namesResult")
            }
        }
    }

    private fun getAnnotatedString(namesResult: NamesResult): AnnotatedString =
        getAnnotatedStringForLink(
            fullText = namesResult.titleSentence,
            hyperLinks = mutableMapOf(
                namesResult.linkedString to namesResult.link,
            ),
            linkTextColor = Color.Blue,
            fontSize = 18.sp,
            linkTextFontWeight = FontWeight.Normal,
            linkTextDecoration = TextDecoration.None,
        )

    private fun String.withOptionalTeamim(): String =
        map {
            when (it) {
                '?' -> it
                else -> "$it$TEAMIM_CHARS?"
            }
        }.joinToString(separator = "", postfix = "$TEAMIM_CHARS?")

    private fun String.makeDageshFirstNikud(): String =
        replaceRegex(regex = "($ALEF_TAV)($NIKUD_CHARS)($DAGESH)", replacement = "$1$3$2")

    // notice the global makeVavAndYudOptional (there is a diff)
    private fun String.makeVavAndYudOptional() =
        replaceRegex(regex = "([וי])", replacement = "$1?")
}
